import { zodResolver } from "@hookform/resolvers/zod";
import {
  CameraIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CreditCardIcon,
  EnvelopeSimpleIcon,
  FloppyDiskIcon,
  GlobeIcon,
  MapPinIcon,
  UserIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import type { UpdateUserDto } from "@reactive-resume/dto";
import { updateUserSchema } from "@reactive-resume/dto";
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import type { Country } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input/max";
import "react-phone-number-input/style.css";
import "flag-icons/css/flag-icons.min.css";

import { UserAvatar } from "@/client/components/user-avatar";
import {
  countries,
  countryContinents,
  countryNotProvided,
  COUNTRY_NOT_PROVIDED_CODE,
  findCurrency,
} from "@/client/constants/country-preferences";
import { useToast } from "@/client/hooks/use-toast";
import { useResendVerificationEmail } from "@/client/services/auth";
import {
  useMyProfile,
  useUpdateMyProfile,
} from "@/client/services/profile/queries";
import { useUploadImage } from "@/client/services/storage";
import { useSaveCountryPreference } from "@/client/services/user/country-preference";
import {
  useUpdateUser,
  useUser,
} from "@/client/services/user";
import {
  type CountryPreference,
  useCountryPreferenceStore,
} from "@/client/stores/country-preference";

export const UserProfileSettings = () => {
  const inputRef =
    useRef<HTMLInputElement>(null);

  const profileSyncedRef =
    useRef(false);

  const queryClient =
    useQueryClient();

  const { user } = useUser();
  const { toast } = useToast();

  const {
    updateUser,
    loading,
  } = useUpdateUser();

  const {
    uploadImage,
    loading: uploading,
  } = useUploadImage();

  const {
    resendVerificationEmail,
  } = useResendVerificationEmail();

  const {
    data: myProfile,
  } = useMyProfile();

  const updateProfessionalProfile =
    useUpdateMyProfile();

  const professionalProfile =
    myProfile?.professionalProfile ??
    null;

  const savedPreference =
    useCountryPreferenceStore(
      (state) => state.preference,
    );

  const setPreference =
    useCountryPreferenceStore(
      (state) => state.setPreference,
    );

  const {
    saveCountryPreference,
    loading: savingCountry,
    error: countryPreferenceError,
    reset: resetCountryPreference,
  } = useSaveCountryPreference();

  const [
    phone,
    setPhone,
  ] = useState("");

  const [
    countryCode,
    setCountryCode,
  ] = useState("");

  useEffect(() => {
    if (
      !myProfile ||
      !user ||
      profileSyncedRef.current
    ) {
      return;
    }

    profileSyncedRef.current = true;

    setPhone(
      myProfile.professionalProfile
        ?.phone ?? "",
    );

    setCountryCode(
      user.countryNotProvided
        ? COUNTRY_NOT_PROVIDED_CODE
        : user.countryCode ??
            savedPreference?.countryCode ??
            "",
    );
  }, [
    myProfile,
    savedPreference,
    user,
  ]);

  const isCountryNotProvided =
    countryCode ===
    COUNTRY_NOT_PROVIDED_CODE;

  const selectedCountry =
    useMemo(() => {
      if (isCountryNotProvided) {
        return countryNotProvided;
      }

      return countries.find(
        (country) =>
          country.countryCode ===
          countryCode,
      );
    }, [
      countryCode,
      isCountryNotProvided,
    ]);

  const selectedCurrency =
    useMemo(
      () =>
        selectedCountry
          ? findCurrency(
              selectedCountry.currencyCode,
            )
          : undefined,
      [selectedCountry],
    );

  const groupedCountries =
    useMemo(
      () =>
        countryContinents
          .map((continent) => ({
            continent,

            countries:
              countries.filter(
                (country) =>
                  country.continent ===
                  continent,
              ),
          }))
          .filter(
            (group) =>
              group.countries.length >
              0,
          ),
      [],
    );

  const phoneCountry =
    selectedCountry &&
    !isCountryNotProvided
      ? (selectedCountry.countryCode as Country)
      : undefined;

  const phoneCountries =
    useMemo<Country[]>(
      () =>
        phoneCountry
          ? [phoneCountry]
          : [],
      [phoneCountry],
    );

  const handleSaveContact =
    async () => {
      if (
        !selectedCountry ||
        !selectedCurrency
      ) {
        toast({
          variant: "error",
          title: "Pays requis",

          description:
            "Sélectionnez votre pays de résidence avant d’enregistrer.",
        });

        return;
      }

      try {
        resetCountryPreference();

        /*
         * La préférence pays est enregistrée
         * sur l’utilisateur.
         *
         * Le backend détermine lui-même la
         * devise afin d’éviter une valeur
         * arbitraire envoyée par le frontend.
         */
        const savedCountry =
          await saveCountryPreference({
            countryCode:
              isCountryNotProvided
                ? null
                : selectedCountry.countryCode,
          });

        const serverCurrency =
          findCurrency(
            savedCountry.pricingCurrencyCode,
          );

        const appliedCurrency =
          serverCurrency ??
          selectedCurrency;

        // Le pays reste sur le compte : la ville du profil n'est plus écrasée.
        await updateProfessionalProfile.mutateAsync(
          {
            phone: phone.trim(),
          },
        );

        const preference:
          CountryPreference = {
          ...selectedCountry,

          countryCode:
            savedCountry.countryCode,

          currencyCode:
            savedCountry.pricingCurrencyCode,

          currencyLabel:
            appliedCurrency.label,

          countryNotProvided:
            savedCountry.countryNotProvided,
        };

        /*
         * Mise à jour immédiate des caches
         * Zustand utilisés par les offres.
         */
        setPreference(preference);

        /*
         * Le compte et les moyens de paiement
         * doivent être immédiatement rechargés.
         */
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["user"],
          }),

          queryClient.invalidateQueries({
            queryKey: [
              "payment-options",
            ],
          }),
        ]);

        toast({
          variant: "success",

          title:
            "Coordonnées mises à jour",

          description:
            "Votre pays, votre devise et votre numéro ont été enregistrés.",
        });
      } catch {
        toast({
          variant: "error",

          title:
            "Enregistrement impossible",

          description:
            countryPreferenceError ??
            "Vérifiez vos informations et réessayez.",
        });
      }
    };

  const completionCriteria = [
    {
      label: "Nom complet",

      done: Boolean(
        user?.name?.trim(),
      ),
    },

    {
      label: "Photo de profil",

      done: Boolean(
        user?.picture,
      ),
    },

    {
      label: "E-mail vérifié",

      done: Boolean(
        user?.emailVerified,
      ),
    },

    {
      label: "Téléphone",

      done: Boolean(
        professionalProfile?.phone?.trim(),
      ),
    },

    {
      label: "Pays de résidence",

      done: Boolean(
        user?.countryPreferenceSetAt,
      ),
    },
  ];

  const completionDone =
    completionCriteria.filter(
      (criterion) => criterion.done,
    ).length;

  const completionPercent =
    Math.round(
      (completionDone /
        completionCriteria.length) *
        100,
    );

  const isProfileComplete =
    completionPercent === 100;

  const missingCriteria =
    completionCriteria.filter(
      (criterion) =>
        !criterion.done,
    );

  const form =
    useForm<UpdateUserDto>({
      resolver: zodResolver(
        updateUserSchema,
      ),

      defaultValues: {
        picture: "",
        name: "",
        email: "",
      },
    });

  useEffect(() => {
    if (!user) {
      return;
    }

    form.reset({
      picture:
        user.picture ?? "",

      name:
        user.name,

      email:
        user.email,
    });
  }, [
    form,
    user,
  ]);

  const handleReset = () => {
    if (!user) {
      return;
    }

    form.reset({
      picture:
        user.picture ?? "",

      name:
        user.name,

      email:
        user.email,
    });
  };

  const handleSubmit = async (
    data: UpdateUserDto,
  ) => {
    if (!user) {
      return;
    }

    const emailChanged =
      user.email !== data.email;

    await updateUser({
      name: data.name,
      email: data.email,
      picture: data.picture,
    });

    form.reset(data);

    toast({
      variant: "success",
      title: "Profil mis à jour",

      description:
        emailChanged
          ? "Consultez votre boîte e-mail pour confirmer votre nouvelle adresse."
          : "Vos informations personnelles ont été enregistrées.",
    });
  };

  const handlePhotoSelection =
    async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file =
        event.target.files?.[0];

      if (!file) {
        return;
      }

      const maximumSize =
        5 * 1024 * 1024;

      if (
        !file.type.startsWith(
          "image/",
        )
      ) {
        toast({
          variant: "error",

          title:
            "Format non pris en charge",

          description:
            "Veuillez sélectionner une image.",
        });

        return;
      }

      if (
        file.size > maximumSize
      ) {
        toast({
          variant: "error",

          title:
            "Image trop volumineuse",

          description:
            "La taille maximale autorisée est de 5 Mo.",
        });

        return;
      }

      try {
        const response =
          await uploadImage(file);

        const picture =
          response.data;

        await updateUser({
          picture,
        });

        form.setValue(
          "picture",
          picture,
          {
            shouldDirty: false,
          },
        );

        toast({
          variant: "success",

          title:
            "Photo mise à jour",

          description:
            "Votre nouvelle photo de profil a été enregistrée.",
        });
      } finally {
        event.target.value = "";
      }
    };

  const handleVerificationEmail =
    async () => {
      const response =
        await resendVerificationEmail();

      toast({
        variant: "success",
        title: "E-mail envoyé",

        description:
          response.message,
      });
    };

  if (!user) {
    return null;
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
      <aside
        className={cn(
          "rounded-2xl border border-slate-200 bg-white",

          "p-5 shadow-[0_8px_30px_rgba(15,23,42,0.05)]",

          "sm:p-6",
        )}
      >
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <UserAvatar
              size={104}
              className="rounded-full ring-4 ring-indigo-50"
            />

            <button
              type="button"
              disabled={uploading}
              onClick={() =>
                inputRef.current?.click()
              }
              aria-label="Modifier la photo de profil"
              className={cn(
                "absolute bottom-0 right-0 grid size-9",

                "place-items-center rounded-full border-2 border-white",

                "bg-indigo-600 text-white shadow-lg",

                "transition-transform hover:scale-105",

                "disabled:cursor-wait disabled:opacity-60",

                "focus-visible:outline-none focus-visible:ring-2",

                "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
              )}
            >
              <CameraIcon
                aria-hidden
                size={17}
                weight="bold"
              />
            </button>

            <input
              ref={inputRef}
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                void handlePhotoSelection(
                  event,
                );
              }}
            />
          </div>

          <h2 className="mt-4 max-w-full truncate text-lg font-bold text-indigo-950">
            {user.name}
          </h2>

          <p className="mt-1 max-w-full truncate text-xs text-slate-500">
            {user.email}
          </p>

          <span
            className={cn(
              "mt-3 inline-flex items-center gap-1.5 rounded-full",

              "px-3 py-1.5 text-xs font-semibold",

              user.emailVerified
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700",
            )}
          >
            {user.emailVerified ? (
              <CheckCircleIcon
                aria-hidden
                size={15}
                weight="fill"
              />
            ) : (
              <WarningCircleIcon
                aria-hidden
                size={15}
                weight="fill"
              />
            )}

            {user.emailVerified
              ? "Profil vérifié"
              : "E-mail non vérifié"}
          </span>
        </div>

        <div className="my-5 h-px bg-slate-200" />

        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">
              Complétude du profil
            </span>

            <strong className="text-lg text-indigo-700">
              {completionPercent} %
            </strong>
          </div>

          <div
            role="progressbar"
            aria-label="Complétude du profil"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={
              completionPercent
            }
            className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"
          >
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",

                isProfileComplete
                  ? "w-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                  : "bg-gradient-to-r from-indigo-600 to-emerald-400",
              )}
              style={{
                width: `${completionPercent}%`,
              }}
            />
          </div>

          {isProfileComplete ? (
            <div className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] font-semibold text-emerald-600">
              <CheckCircleIcon
                aria-hidden
                size={13}
                weight="fill"
              />

              Profil complété — 50
              points de fidélité
            </div>
          ) : (
            <>
              <p className="mt-3 text-[11px] font-semibold text-slate-600">
                {completionDone}/
                {
                  completionCriteria.length
                }{" "}
                — il reste :
              </p>

              <ul className="mt-1.5 space-y-1">
                {missingCriteria.map(
                  (criterion) => (
                    <li
                      key={
                        criterion.label
                      }
                      className="flex items-center gap-1.5 text-[11px] text-slate-500"
                    >
                      <WarningCircleIcon
                        aria-hidden
                        size={12}
                        weight="fill"
                        className="shrink-0 text-amber-500"
                      />

                      {
                        criterion.label
                      }
                    </li>
                  ),
                )}
              </ul>

              <p className="mt-2 text-center text-[11px] leading-4 text-slate-500">
                Un profil complet
                débloque 50 points de
                fidélité.
              </p>
            </>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() =>
            inputRef.current?.click()
          }
          className="mt-5 w-full rounded-xl text-xs"
        >
          <CameraIcon
            aria-hidden
            className="mr-2"
            size={16}
          />

          {uploading
            ? "Importation…"
            : "Modifier la photo"}
        </Button>
      </aside>

      <section
        className={cn(
          "min-w-0 rounded-2xl border border-slate-200 bg-white",

          "p-4 shadow-[0_8px_30px_rgba(15,23,42,0.05)]",

          "sm:p-6 lg:p-7",
        )}
      >
        <div className="border-b border-slate-200 pb-5">
          <h2 className="text-base font-bold text-indigo-950">
            Informations personnelles
          </h2>

          <p className="mt-1 text-xs leading-5 text-slate-500">
            Ces informations sont
            utilisées dans votre espace
            Mon CV Pro.
          </p>
        </div>

        <Form {...form}>
          <form
            className="mt-5"
            onSubmit={form.handleSubmit(
              handleSubmit,
            )}
          >
            <FormField
              name="picture"
              control={form.control}
              render={({
                field,
              }) => (
                <input
                  type="hidden"
                  name={
                    field.name
                  }
                  value={
                    field.value ??
                    ""
                  }
                  onChange={
                    field.onChange
                  }
                />
              )}
            />

            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                name="name"
                control={
                  form.control
                }
                render={({
                  field,
                }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-indigo-950">
                      Nom complet
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <UserIcon
                          aria-hidden
                          size={17}
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />

                        <Input
                          {...field}
                          autoComplete="name"
                          placeholder="Votre nom complet"
                          className="h-11 rounded-xl pl-10 text-sm"
                        />
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                name="email"
                control={
                  form.control
                }
                render={({
                  field,
                }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-indigo-950">
                      Adresse e-mail
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <EnvelopeSimpleIcon
                          aria-hidden
                          size={17}
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />

                        <Input
                          {...field}
                          type="email"
                          autoComplete="email"
                          placeholder="votre@email.com"
                          className="h-11 rounded-xl pl-10 pr-10 text-sm lowercase"
                        />

                        {user.emailVerified && (
                          <CheckCircleIcon
                            aria-hidden
                            size={17}
                            weight="fill"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"
                          />
                        )}
                      </div>
                    </FormControl>

                    <FormDescription className="text-[11px]">
                      {user.emailVerified ? (
                        <span className="text-emerald-600">
                          Cette adresse a
                          été vérifiée.
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            void handleVerificationEmail();
                          }}
                          className="font-semibold text-indigo-600 hover:underline"
                        >
                          Renvoyer
                          l’e-mail de
                          vérification
                        </button>
                      )}
                    </FormDescription>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-7 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={
                  !form.formState
                    .isDirty ||
                  loading
                }
                onClick={
                  handleReset
                }
                className="rounded-xl text-xs"
              >
                Annuler
              </Button>

              <Button
                type="submit"
                disabled={
                  !form.formState
                    .isDirty ||
                  loading
                }
                className="rounded-xl bg-indigo-600 text-xs hover:bg-indigo-700"
              >
                <FloppyDiskIcon
                  aria-hidden
                  className="mr-2"
                  size={16}
                />

                {loading
                  ? "Enregistrement…"
                  : "Enregistrer les modifications"}
              </Button>
            </div>
          </form>
        </Form>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <div>
            <h2 className="text-base font-bold text-indigo-950">
              Coordonnées
            </h2>

            <p className="mt-1 text-xs leading-5 text-slate-500">
              Choisissez votre pays de
              résidence. La devise et
              l’indicatif téléphonique
              seront appliqués
              automatiquement à votre
              compte.
            </p>
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <label
                htmlFor="profile-country"
                className="text-xs font-semibold text-indigo-950"
              >
                Pays de résidence
              </label>

              <div className="relative">
                {selectedCountry ? (
                  isCountryNotProvided ? (
                    <GlobeIcon
                      aria-hidden
                      size={17}
                      weight="duotone"
                      className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-indigo-500"
                    />
                  ) : (
                    <span className="pointer-events-none absolute left-3 top-1/2 z-10 grid size-5 -translate-y-1/2 place-items-center overflow-hidden rounded-full bg-white shadow-sm">
                      <span
                        aria-hidden
                        className={`fi fi-${selectedCountry.flagCode} size-full rounded-full bg-cover bg-center`}
                      />
                    </span>
                  )
                ) : (
                  <MapPinIcon
                    aria-hidden
                    size={17}
                    weight="duotone"
                    className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-slate-400"
                  />
                )}

                <select
                  id="profile-country"
                  value={
                    countryCode
                  }
                  disabled={
                    savingCountry ||
                    updateProfessionalProfile.isPending
                  }
                  onChange={(
                    event,
                  ) => {
                    resetCountryPreference();

                    setCountryCode(
                      event.target
                        .value,
                    );

                    /*
                     * Un ancien numéro ne
                     * doit pas rester associé
                     * au nouveau pays.
                     */
                    setPhone("");
                  }}
                  className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm font-semibold text-indigo-950 outline-none transition hover:border-indigo-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/70 disabled:cursor-wait disabled:opacity-60"
                >
                  <option
                    value=""
                    disabled
                  >
                    Sélectionnez
                    votre pays
                  </option>

                  <option
                    value={
                      COUNTRY_NOT_PROVIDED_CODE
                    }
                  >
                    MON PAYS N’EST
                    PAS RENSEIGNÉ
                  </option>

                  {groupedCountries.map(
                    (group) => (
                      <optgroup
                        key={
                          group.continent
                        }
                        label={
                          group.continent
                        }
                      >
                        {group.countries.map(
                          (
                            country,
                          ) => (
                            <option
                              key={
                                country.countryCode
                              }
                              value={
                                country.countryCode ??
                                ""
                              }
                            >
                              {
                                country.countryName
                              }
                            </option>
                          ),
                        )}
                      </optgroup>
                    ),
                  )}
                </select>
              </div>

              <p className="text-[11px] leading-4 text-slate-500">
                Ce pays déterminera la
                devise et les moyens de
                paiement disponibles.
              </p>
            </div>

            <div className="space-y-1.5">
              <span className="block text-xs font-semibold text-indigo-950">
                Devise appliquée
              </span>

              <div
                aria-live="polite"
                className={cn(
                  "flex min-h-11 items-center gap-2.5 rounded-xl border px-3 py-2",

                  selectedCountry
                    ? "border-indigo-200 bg-gradient-to-br from-indigo-50/90 to-white shadow-[0_6px_18px_rgba(79,70,229,0.07)]"
                    : "border-slate-200 bg-slate-50",
                )}
              >
                {selectedCountry &&
                selectedCurrency ? (
                  <>
                    <span className="grid size-7 shrink-0 place-items-center overflow-hidden rounded-full bg-white shadow-sm">
                      {isCountryNotProvided ? (
                        <GlobeIcon
                          aria-hidden
                          size={17}
                          weight="duotone"
                          className="text-indigo-600"
                        />
                      ) : (
                        <span
                          aria-hidden
                          className={`fi fi-${selectedCountry.flagCode} size-full rounded-full bg-cover bg-center`}
                        />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-xs font-extrabold text-indigo-950">
                        {
                          selectedCurrency.label
                        }
                      </strong>

                      <small className="block truncate text-[10px] font-medium text-slate-500">
                        {isCountryNotProvided
                          ? "Devise appliquée par défaut"
                          : `Prix et paiements pour ${selectedCountry.countryName}`}
                      </small>
                    </span>

                    <span className="rounded-full border border-indigo-200 bg-white px-2 py-1 text-[10px] font-black tracking-wide text-indigo-700">
                      {
                        selectedCurrency.code
                      }
                    </span>
                  </>
                ) : (
                  <span className="text-xs font-medium text-slate-400">
                    La devise apparaîtra
                    après la sélection du
                    pays.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-1.5">
            <label
              htmlFor="profile-phone"
              className="text-xs font-semibold text-indigo-950"
            >
              Numéro de téléphone
            </label>

            {phoneCountry ? (
              <PhoneInput
                key={
                  phoneCountry
                }
                id="profile-phone"
                international
                countryCallingCodeEditable={
                  false
                }
                defaultCountry={
                  phoneCountry
                }
                countries={
                  phoneCountries
                }
                disabled={
                  savingCountry ||
                  updateProfessionalProfile.isPending
                }
                value={phone}
                onChange={(
                  value,
                ) =>
                  setPhone(
                    value ?? "",
                  )
                }
                inputComponent={
                  Input
                }
                placeholder="Saisissez votre numéro de téléphone"
                className={[
                  "max-w-2xl",

                  "[&_.PhoneInputCountry]:h-11",

                  "[&_.PhoneInputCountry]:rounded-l-xl",

                  "[&_.PhoneInputCountry]:border",

                  "[&_.PhoneInputCountry]:border-r-0",

                  "[&_.PhoneInputCountry]:border-slate-200",

                  "[&_.PhoneInputCountry]:bg-slate-50",

                  "[&_.PhoneInputCountry]:px-3",

                  "[&_.PhoneInputCountrySelect]:pointer-events-none",

                  "[&_.PhoneInputCountrySelectArrow]:hidden",

                  "[&_.PhoneInputInput]:h-11",

                  "[&_.PhoneInputInput]:rounded-l-none",

                  "[&_.PhoneInputInput]:rounded-r-xl",

                  "[&_.PhoneInputInput]:border",

                  "[&_.PhoneInputInput]:border-slate-200",

                  "[&_.PhoneInputInput]:px-3",

                  "[&_.PhoneInputInput]:text-sm",

                  "[&_.PhoneInputInput:focus]:border-indigo-400",

                  "[&_.PhoneInputInput:focus]:ring-2",

                  "[&_.PhoneInputInput:focus]:ring-indigo-100",
                ].join(" ")}
              />
            ) : (
              <Input
                id="profile-phone"
                disabled
                value=""
                placeholder="Sélectionnez d’abord votre pays"
                className="h-11 max-w-2xl rounded-xl border-slate-200 bg-slate-50 text-sm"
              />
            )}

            <p className="text-[11px] leading-4 text-slate-500">
              Le drapeau et l’indicatif
              sont définis depuis votre
              pays de résidence.
            </p>
          </div>

          {isCountryNotProvided && (
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50/70 px-3.5 py-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-indigo-600 text-white">
                <CreditCardIcon
                  aria-hidden
                  size={17}
                  weight="duotone"
                />
              </span>

              <div>
                <strong className="text-xs font-bold text-indigo-950">
                  Paiement par carte
                  bancaire uniquement
                </strong>

                <p className="mt-0.5 text-[11px] leading-4 text-slate-600">
                  Sans pays renseigné,
                  les prix sont affichés
                  en euros et le paiement
                  mobile n’est pas
                  proposé.
                </p>
              </div>
            </div>
          )}

          {countryPreferenceError && (
            <div
              role="alert"
              className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
            >
              {
                countryPreferenceError
              }
            </div>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p
              className={cn(
                "text-[11px]",

                isProfileComplete
                  ? "text-emerald-600"
                  : "text-slate-500",
              )}
            >
              {isProfileComplete
                ? "Profil complet ✓"
                : "Téléphone + pays de résidence = profil 100 % complet"}
            </p>

            <Button
              type="button"
              disabled={
                !selectedCountry ||
                !selectedCurrency ||
                savingCountry ||
                updateProfessionalProfile.isPending
              }
              onClick={() => {
                void handleSaveContact();
              }}
              className="w-full rounded-xl text-xs sm:w-auto"
            >
              {savingCountry ||
              updateProfessionalProfile.isPending ? (
                <CircleNotchIcon
                  aria-hidden
                  className="mr-2 animate-spin"
                  size={16}
                />
              ) : (
                <FloppyDiskIcon
                  aria-hidden
                  className="mr-2"
                  size={16}
                />
              )}

              Enregistrer mes
              coordonnées
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};